import { RestoreFromTrash as RestoreFromTrashIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import {
  BooleanInput,
  Button,
  Create,
  SelectInput,
  SimpleForm,
  TextInput,
  required,
  useDataProvider,
  useInput,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { IconPicker } from '../../components/IconPicker';
import { Command, CommandParameterDraft } from '../../types/commands';
import { saveDraftParameters } from './draftParameters';
import { DraftParametersPanel } from './DraftParametersPanel';
import { useRestoreCommand } from './useRestoreCommand';

// Wrapper to make IconPicker compatible with React Admin
const IconPickerInput = (props: any) => {
  const { field } = useInput(props);
  return <IconPicker {...field} label={props.label} helperText={props.helperText} />;
};

/**
 * `POST /v3/api/commands` answers 422 with a `trashed_command` when the name is
 * still held by a soft-deleted command. Offer a one-click restore instead of
 * leaving the user with a dead-end validation error.
 */
const TrashedCommandConflictAlert = ({ command }: { command: Command }) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const { restore, isRestoring } = useRestoreCommand();

  const handleRestore = async () => {
    if (await restore(command.id, command.name)) {
      redirect('edit', 'commands', command.id);
    }
  };

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2, width: '100%' }}
      action={
        <Button
          onClick={handleRestore}
          disabled={isRestoring}
          variant="contained"
          startIcon={<RestoreFromTrashIcon />}
          label="resources.commands.restore.action"
        />
      }
    >
      <AlertTitle>{translate('resources.commands.create.name_taken_by_deleted_title')}</AlertTitle>
      {translate('resources.commands.create.name_taken_by_deleted', { name: command.name })}
    </Alert>
  );
};

export const CommandCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [waitType, setWaitType] = useState<'done_message' | 'topic_message'>('done_message');
  const [parameters, setParameters] = useState<CommandParameterDraft[]>([]);
  const [trashedConflict, setTrashedConflict] = useState<Command | null>(null);

  // The submit handler runs outside the render that owns `parameters`, so keep a
  // ref in sync to guarantee the saved order is the order shown on screen.
  const parametersRef = useRef<CommandParameterDraft[]>([]);

  const handleParametersChange = useCallback((next: CommandParameterDraft[]) => {
    parametersRef.current = next;
    setParameters(next);
  }, []);

  /**
   * The command is created first, then every parameter is POSTed to the new
   * command in display order. A parameter that fails does not invalidate the
   * command, so report exactly which ones made it and which ones did not
   * instead of a blanket success, and land on the edit page where the missing
   * ones can be added.
   */
  const handleCreated = useCallback(
    async (command: Command) => {
      const { saved, failed } = await saveDraftParameters(dataProvider, command.id, parametersRef.current);

      if (failed.length === 0) {
        notify(
          saved.length > 0 ? 'resources.commands.create.created_with_parameters' : 'resources.commands.create.created',
          {
            type: 'info',
            messageArgs: { name: command.name, count: saved.length },
          },
        );
      } else {
        notify('resources.commands.create.parameters_partially_saved', {
          type: 'error',
          multiLine: true,
          autoHideDuration: null,
          messageArgs: {
            name: command.name,
            saved: saved.length > 0 ? saved.join(', ') : translate('resources.commands.create.none'),
            failed: failed.join(', '),
          },
        });
      }

      redirect('edit', 'commands', command.id);
    },
    [dataProvider, notify, redirect, translate],
  );

  return (
    <Create
      mutationOptions={{
        onSuccess: (command: Command) => {
          setTrashedConflict(null);
          return handleCreated(command);
        },
        onError: (error: any) => {
          const trashedCommand = error?.body?.trashed_command as Command | undefined;
          setTrashedConflict(trashedCommand ?? null);

          if (!trashedCommand && !error?.body?.errors) {
            notify(error?.message || 'ra.notification.http_error', { type: 'error' });
          }
        },
      }}
    >
      <SimpleForm>
        {trashedConflict && <TrashedCommandConflictAlert command={trashedConflict} />}
        <TextInput source="name" validate={[required()]} fullWidth />
        <TextInput source="display_name" validate={[required()]} fullWidth label="Display Name" />
        <TextInput source="description" validate={[required()]} multiline rows={3} fullWidth />
        <SelectInput
          source="category"
          choices={[
            { id: 'system', name: 'System' },
            { id: 'application', name: 'Application' },
            { id: 'network', name: 'Network' },
            { id: 'diagnostics', name: 'Diagnostics' },
          ]}
          validate={[required()]}
          defaultValue="system"
        />
        <SelectInput
          source="danger_level"
          choices={[
            { id: 'low', name: 'Low' },
            { id: 'medium', name: 'Medium' },
            { id: 'high', name: 'High' },
            { id: 'critical', name: 'Critical' },
          ]}
          validate={[required()]}
          defaultValue="low"
        />
        <IconPickerInput source="icon" label="Icon" helperText="Select an icon for this command" />
        <TextInput
          source="mqtt_topic_pattern"
          validate={[required()]}
          fullWidth
          label="MQTT Topic Pattern"
          helperText="e.g., CMD/{hostname}/{command}"
          defaultValue="CMD/{hostname}/{command}"
        />
        <BooleanInput source="is_active" defaultValue={true} />
        <BooleanInput source="confirmation_required" defaultValue={false} />

        {/* Feedback Configuration Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Feedback Configuration
          </Typography>
          <BooleanInput
            source="feedback"
            label="Enable Real-time Feedback"
            helperText="Monitor command execution and show real-time output"
            onChange={(e) => setFeedbackEnabled(e.target.checked)}
          />
        </Box>

        {feedbackEnabled && (
          <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
            <SelectInput
              source="feedback_config.wait_type"
              label="Wait For"
              choices={[
                {
                  id: 'done_message',
                  name: 'Done Message on CMD_ACK/{hostname}',
                },
                {
                  id: 'topic_message',
                  name: 'Custom Topic/Message',
                },
              ]}
              validate={feedbackEnabled ? [required()] : []}
              defaultValue="done_message"
              onChange={(e) => setWaitType(e.target.value as 'done_message' | 'topic_message')}
              helperText="What should the system wait for to confirm command completion?"
            />

            {waitType === 'topic_message' && (
              <>
                <TextInput
                  source="feedback_config.custom_topic"
                  label="Custom Topic Pattern"
                  fullWidth
                  helperText="e.g., UP/{hostname} or STATUS/{hostname}"
                  validate={waitType === 'topic_message' ? [required()] : []}
                />
                <TextInput
                  source="feedback_config.custom_message"
                  label="Expected Message/Payload"
                  fullWidth
                  multiline
                  rows={2}
                  helperText='JSON payload to wait for, e.g., {"status":"ready"} or use {command} placeholder'
                />
              </>
            )}

            <BooleanInput
              source="feedback_config.show_terminal"
              label="Show Terminal Output"
              defaultValue={true}
              helperText="Display real-time command output in a terminal window"
            />
          </Box>
        )}

        <DraftParametersPanel parameters={parameters} onChange={handleParametersChange} />
      </SimpleForm>
    </Create>
  );
};
